/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Stack, Typography, Alert, Divider } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { EditableHotkey } from "../EditableHotkey";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { getModifierKey } from "hooks/usePlatform";
import { useAreaHotkeys } from "components/scrapEyeLabelling/hooks/useAreaHotkeys";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import { useMemo } from "react";

export const AreaMappingGuide = () => {
  const { t } = useTenantTranslation();
  const { getDisplayKey } = useHotkeyContext();
  const { alt } = getModifierKey();
  const areaHotkeys = useAreaHotkeys();

  // Get first two area hotkeys for examples
  const exampleAreaHotkeys = useMemo(() => {
    const entries = Object.entries(areaHotkeys ?? {});
    if (entries.length === 0) return [];
    return entries.slice(0, 2).map(([areaName, hotkey]) => ({
      areaName,
      hotkey: hotkey.toUpperCase(),
      formattedAreaName: formatArea(areaName),
    }));
  }, [areaHotkeys]);

  return (
    <Stack spacing={4}>
      <Typography variant="h6">{t("areaMappingGuideTitle")}</Typography>

      <Alert severity="info" icon={<InfoIcon />}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          {t("areaMappingBenefits")}
        </Typography>
        <Typography variant="body2">
          {t("areaMappingBenefitsDescription")}
        </Typography>
      </Alert>

      <Stack spacing={3}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t("howToUseAreaMapping")}
        </Typography>

        <Box>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <EditableHotkey hotkeyId="areaMaterialMapping" />
            <Typography variant="body2">
              {t("areaMappingStep1", {
                hotkey: getDisplayKey("areaMaterialMapping"),
              })}
            </Typography>
          </Stack>

          <Stack spacing={1.5} pl={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2">{t("areaMappingStep2")}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2">{t("areaMappingStep3")}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2">{t("areaMappingStep4")}</Typography>
            </Stack>
          </Stack>
        </Box>

        <Alert severity="warning">
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t("areaMappingImportantNote")}
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              {t("areaMappingOnlyLabelVisible")}
            </Typography>
            <Typography variant="body2">
              {t("areaMappingKeepUnknown")}
            </Typography>
          </Stack>
        </Alert>
      </Stack>

      <Divider />

      <Stack spacing={3}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t("updatingAreaMapping")}
        </Typography>

        <Typography variant="body2">
          {t("updatingAreaMappingDescription", {
            hotkey: getDisplayKey("areaMaterialMapping"),
          })}
        </Typography>

        <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
          <Typography variant="caption" display="block" gutterBottom>
            {t("exampleScenario")}
          </Typography>
          <Typography variant="body2">
            {t("areaMappingExampleScenario")}
          </Typography>
        </Box>
      </Stack>

      <Divider />

      <Stack spacing={3}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t("quickAreaEventShortcut")}
        </Typography>

        <Typography variant="body2">
          {t("quickAreaEventDescription", { alt })}
        </Typography>

        {exampleAreaHotkeys.length > 0 && (
          <Stack spacing={1.5} pl={2}>
            {exampleAreaHotkeys.map((example) => (
              <Typography key={example.areaName} variant="body2">
                •{" "}
                {t("quickAreaEventExampleWithArea", {
                  hotkey: example.hotkey,
                  areaName: example.formattedAreaName,
                })}
              </Typography>
            ))}
          </Stack>
        )}

        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {t("quickAreaEventTypeExplanation", {
            hotkey: getDisplayKey("reverseLiftDrop"),
          })}
        </Typography>

        <Alert severity="success" icon={<InfoIcon />}>
          <Typography variant="body2">{t("quickAreaEventBenefit")}</Typography>
        </Alert>
      </Stack>
    </Stack>
  );
};
